import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './SubscriptionManagement.css';
import { useAuth } from '../../context/AuthContext';
import subscriptionService from '../../services/subscription.service';
import Spinner from '../Spinner/Spinner';
import { Button } from 'react-bootstrap';

const SubscriptionManagement = ({ externalSubscription }) => {
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        setLoading(true);
        // If external subscription data is provided, use that instead
        if (externalSubscription) {
          console.log('Using external subscription data:', externalSubscription);
          setSubscription(externalSubscription);
          setLoading(false);
          return;
        }
        
        // Otherwise fetch subscription data
        const response = await subscriptionService.getStatus();
        setSubscription(response.data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        toast.error('Failed to load subscription details');
      } finally {
        setLoading(false);
      }
    };

    // Immediately set subscription from props if available
    if (externalSubscription) {
      setSubscription(externalSubscription);
      setLoading(false);
    }
    // Otherwise fetch if user is authenticated
    else if (currentUser) {
      fetchSubscriptionStatus();
    }
  }, [currentUser, externalSubscription]);

  const handleOpenPortal = async () => {
    try {
      setPortalLoading(true);
      const response = await subscriptionService.getPortalSession();
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to open customer portal');
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      await subscriptionService.cancelSubscription();
      toast.success('Subscription cancelled successfully');
      
      // Refresh subscription status
      const response = await subscriptionService.getStatus();
      setSubscription(response.data);
      setCancelLoading(false);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
      setCancelLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      console.log('Creating checkout session for subscription upgrade');
      const response = await subscriptionService.createCheckoutSession();
      
      console.log('Checkout session response:', response);
      
      // Handle different response structures
      let redirectUrl;
      if (response?.data?.data?.url) {
        // Nested data structure: { data: { data: { url: '...' } } }
        redirectUrl = response.data.data.url;
      } else if (response?.data?.url) {
        // Direct data structure: { data: { url: '...' } }
        redirectUrl = response.data.url;
      } else if (typeof response?.data === 'string' && response.data.includes('http')) {
        // Plain URL string
        redirectUrl = response.data;
      }
      
      if (redirectUrl) {
        // Redirect to Stripe Checkout
        console.log('Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.error('Invalid checkout response format:', response);
        throw new Error('Invalid checkout session response');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start upgrade process. Please try again.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-management-container">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-header bg-secondary text-white d-flex align-items-center">
        <i className="bi bi-credit-card me-2"></i>
        <h5 className="mb-0">Your Subscription</h5>
      </div>
      
      <div className="card-body p-4">
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div className="fw-bold">Current Plan:</div>
          <div className="d-flex align-items-center">
            <span className={`badge ${subscription?.status === 'active' ? 'bg-success' : 'bg-primary'} p-2 me-2`}>
              {subscription?.status === 'active' ? 'Pro' : 'Free'}
            </span>
            {subscription?.cancelAtPeriodEnd && subscription?.status === 'active' && (
              <span className="badge bg-warning text-dark">
                Cancels at period end
              </span>
            )}
          </div>
        </div>

        {subscription?.status === 'active' && (
          <>
            <hr className="my-3" />
            
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div className="fw-bold">Billing Period:</div>
              <span className="badge bg-light text-dark p-2">
                {subscription.interval === 'month' ? 'Monthly' : 'Yearly'}
              </span>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold">Next Billing Date:</div>
              <span className="badge bg-light text-dark p-2">
                {formatDate(subscription.currentPeriodEnd)}
              </span>
            </div>
          </>
        )}
        
        <hr className="my-4" />
        
        <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
          {subscription?.status === 'active' ? (
            <>
              <Button 
                variant="outline-primary" 
                className="d-flex align-items-center" 
                onClick={handleOpenPortal}
                disabled={portalLoading}
                size="lg"
              >
                {portalLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-credit-card-2-front me-2"></i>
                    Manage Payment Methods
                  </>
                )}
              </Button>
              
              {!subscription.cancelAtPeriodEnd && (
                <Button 
                  variant="outline-danger" 
                  className="d-flex align-items-center ms-2" 
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-2"></i>
                      Cancel Subscription
                    </>
                  )}
                </Button>
              )}
            </>
          ) : (
            <Button 
              variant="success" 
              className="d-flex align-items-center justify-content-center" 
              onClick={handleUpgrade}
              disabled={loading}
              size="lg"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-up-circle me-2"></i>
                  Upgrade to Pro
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
